import { useState, useCallback } from 'react';
import type { Blog, BlogPost, BlogStats, Site, BlogPostStatus } from '../../schemas/validation';
import type { BlogCustomColors, BlogSettingsForm } from './types';

export interface BlogManagerState {
  // Data state
  site: Site | null;
  setSite: React.Dispatch<React.SetStateAction<Site | null>>;
  blog: Blog | null;
  setBlog: React.Dispatch<React.SetStateAction<Blog | null>>;
  stats: BlogStats | null;
  setStats: React.Dispatch<React.SetStateAction<BlogStats | null>>;
  posts: BlogPost[];
  setPosts: React.Dispatch<React.SetStateAction<BlogPost[]>>;
  tags: Array<{ tag: string; count: number }>;
  setTags: React.Dispatch<React.SetStateAction<Array<{ tag: string; count: number }>>>;

  // Loading states
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadingPosts: boolean;
  setIsLoadingPosts: React.Dispatch<React.SetStateAction<boolean>>;
  isDeletingPost: boolean;
  setIsDeletingPost: React.Dispatch<React.SetStateAction<boolean>>;
  isTogglingPublish: string | null;
  setIsTogglingPublish: React.Dispatch<React.SetStateAction<string | null>>;
  isTogglingBlogPublish: boolean;
  setIsTogglingBlogPublish: React.Dispatch<React.SetStateAction<boolean>>;

  // UI state
  showDeleteModal: boolean;
  setShowDeleteModal: React.Dispatch<React.SetStateAction<boolean>>;
  postToDelete: BlogPost | null;
  setPostToDelete: React.Dispatch<React.SetStateAction<BlogPost | null>>;
  showSettingsPanel: boolean;
  setShowSettingsPanel: React.Dispatch<React.SetStateAction<boolean>>;
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  statusFilter: 'all' | BlogPostStatus;
  setStatusFilter: React.Dispatch<React.SetStateAction<'all' | BlogPostStatus>>;
  selectedTag: string | null;
  setSelectedTag: React.Dispatch<React.SetStateAction<string | null>>;

  // Settings form state
  isSavingSettings: boolean;
  setIsSavingSettings: React.Dispatch<React.SetStateAction<boolean>>;
  settingsForm: BlogSettingsForm;
  setSettingsForm: React.Dispatch<React.SetStateAction<BlogSettingsForm>>;

  // Custom colors state
  customColors: BlogCustomColors;
  setCustomColors: React.Dispatch<React.SetStateAction<BlogCustomColors>>;
  showColorPanel: boolean;
  setShowColorPanel: React.Dispatch<React.SetStateAction<boolean>>;

  // Pagination
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  totalPages: number;
  setTotalPages: React.Dispatch<React.SetStateAction<number>>;
  totalPosts: number;
  setTotalPosts: React.Dispatch<React.SetStateAction<number>>;

  // Toast state
  showToast: boolean;
  setShowToast: React.Dispatch<React.SetStateAction<boolean>>;
  toastMessage: string;
  setToastMessage: React.Dispatch<React.SetStateAction<string>>;
  toastType: 'success' | 'error';
  setToastType: React.Dispatch<React.SetStateAction<'success' | 'error'>>;

  // SEO state
  showSeoPanel: boolean;
  setShowSeoPanel: React.Dispatch<React.SetStateAction<boolean>>;

  // Media Library state
  showMediaLibrary: boolean;
  setShowMediaLibrary: React.Dispatch<React.SetStateAction<boolean>>;

  // Helpers
  showNotification: (message: string, type: 'success' | 'error') => void;
}

export const useBlogManagerState = (): BlogManagerState => {
  // Data state
  const [site, setSite] = useState<Site | null>(null);
  const [blog, setBlog] = useState<Blog | null>(null);
  const [stats, setStats] = useState<BlogStats | null>(null);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [tags, setTags] = useState<Array<{ tag: string; count: number }>>([]);

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [isTogglingPublish, setIsTogglingPublish] = useState<string | null>(null);
  const [isTogglingBlogPublish, setIsTogglingBlogPublish] = useState(false);

  // UI state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState<BlogPost | null>(null);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | BlogPostStatus>('all');
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  // Settings form state
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState<BlogSettingsForm>({
    title: '',
    description: '',
    basePath: '',
    layout: 'grid',
    layoutId: null,
    showLayout: true,
    postsPerPage: 10,
    showAuthor: true,
    showDate: true,
    showReadingTime: true,
    showFeaturedImage: true,
    showTags: true,
  });

  // Custom colors state
  const [customColors, setCustomColors] = useState<BlogCustomColors>({});
  const [showColorPanel, setShowColorPanel] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  // Toast state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  // SEO state
  const [showSeoPanel, setShowSeoPanel] = useState(false);

  // Media Library state
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);

  const showNotification = useCallback((message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }, []);

  return {
    site,
    setSite,
    blog,
    setBlog,
    stats,
    setStats,
    posts,
    setPosts,
    tags,
    setTags,
    isLoading,
    setIsLoading,
    isLoadingPosts,
    setIsLoadingPosts,
    isDeletingPost,
    setIsDeletingPost,
    isTogglingPublish,
    setIsTogglingPublish,
    isTogglingBlogPublish,
    setIsTogglingBlogPublish,
    showDeleteModal,
    setShowDeleteModal,
    postToDelete,
    setPostToDelete,
    showSettingsPanel,
    setShowSettingsPanel,
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    selectedTag,
    setSelectedTag,
    isSavingSettings,
    setIsSavingSettings,
    settingsForm,
    setSettingsForm,
    customColors,
    setCustomColors,
    showColorPanel,
    setShowColorPanel,
    currentPage,
    setCurrentPage,
    totalPages,
    setTotalPages,
    totalPosts,
    setTotalPosts,
    showToast,
    setShowToast,
    toastMessage,
    setToastMessage,
    toastType,
    setToastType,
    showSeoPanel,
    setShowSeoPanel,
    showMediaLibrary,
    setShowMediaLibrary,
    showNotification,
  };
};
